import {
    useCallback, useMemo,
} from 'react'
import Grid from '@mui/material/Grid'
import isEmpty from 'lodash/isEmpty'

import AdvancedList, {
    ListColumn,
} from 'app/shared-components/AdvancedList/AdvancedList'
import useEnumValues from 'app/hooks/useEnumValues'
import Select from 'app/shared-components/Select/SelectContainer'
import InputSingleline from 'app/shared-components/InputSingleline'
import useLabel from 'app/hooks/useLabel'

import {
    ContainerAvailabilityAllowedProductTypes,
} from 'app/enums/label.enums'
import {
    ProductMetricsType,
} from '../../serviceCenters.types'
import useStyles from './ProductMetrics.style'

const defaultValue = {
    productType: undefined,
    safeMinimumPreconStock: undefined,
    safeMinimumUsableStock: 0,
    processingRatio: undefined,
}

const getColumns = (l: (l: string) => string): ListColumn[] => {
    return [
        {
            colSpan: 6,
            label: l('ProductMetrics.type'),
        },
        {
            colSpan: 'grow',
            label: l('ProductMetrics.safeMinimumPreconStock'),
            align: 'right',
        },
        {
            colSpan: 'grow',
            label: l('ProductMetrics.safeMinimumUsableStock'),
            align: 'right',
        },
        {
            colSpan: 'grow',
            label: l('ProductMetrics.processingRatio'),
            align: 'right',
        },
    ]
}

const defaultProps = {
    disabled: false,
    required: false,
    exists: false,
    showFields: {},
    disabledFields: {},
    value: undefined,
}

const ProductMetrics = ({
    value,
    onChange,
    disabled,
    required,
    exists,
    showFields,
    disabledFields,
}: {
    value?: ProductMetricsType[],
    onChange: (val: ProductMetricsType) => void,
    disabled?: boolean,
    showFields?: Record<string, boolean>
    disabledFields?: Record<string, boolean>
    required?: boolean,
    exists?: boolean,
}) => {
    const {
        classes,
    } = useStyles()
    const allowedValues = useEnumValues()(ContainerAvailabilityAllowedProductTypes)

    const l = useLabel()

    const columns = useMemo(() => {
        return getColumns(l)
    }, [l])

    const onItemCellChange = useCallback(({
        item, newValue, index, handleChange,
    }) => {
        let newItem = {
            ...item,
            ...newValue,
        }

        // if CRT product type, remaining fields should be undefined and disabled

        if (newValue?.productType?.match(/CRT/i)) {
            newItem = {
                ...newValue,
            }
        }

        handleChange(newItem, index)
    }, [])

    const validateItemForAdd = useCallback((item: ProductMetricsType): boolean => {
        return !isEmpty(item) && Object.values(item).every((el) => {
            return Boolean(el) || el === 0
        })
    }, [])

    const renderItem = useCallback(({
        index,
        item,
        handleChange,
    }) => {
        const isCRTProduct = item?.productType?.match(/CRT/i)

        return (
            <>
                <Grid
                    key={`row-container-${index}`}
                    size={columns[0].colSpan}
                    p={1}
                >
                    <Select
                        name="productType"
                        allowedValues={allowedValues}
                        value={item?.productType}
                        onChange={(newValue) => {
                            return onItemCellChange({
                                newValue: {
                                    productType: newValue,
                                },
                                index,
                                item,
                                handleChange,
                            })
                        }}
                        required={required}
                        disabled={
                            !showFields.productType
                            && ((disabledFields.productType && item.productType) || disabled)
                        }
                    />
                </Grid>
                <Grid
                    key={`row-safeMinimumPreconStock-${index}`}
                    size={columns[1].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="safeMinimumPreconStock"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    safeMinimumPreconStock: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        value={item?.safeMinimumPreconStock}
                        required={required}
                        disabled={isCRTProduct || (!showFields.safeMinimumPreconStock
                            && (disabled || disabledFields.safeMinimumPreconStock))}
                    />
                </Grid>
                <Grid
                    key={`row-safeMinimumUsableStock-${index}`}
                    size={columns[2].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="safeMinimumUsableStock"
                        type="number"
                        min={0}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    safeMinimumUsableStock: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        classNames={{
                            text: item.usableContainersAmount < item.safeMinimumUsableStock
                                ? classes.red : classes.green,
                        }}
                        value={item?.safeMinimumUsableStock}
                        required={required}
                        disabled={isCRTProduct || (!showFields.safeMinimumUsableStock
                            && (disabled || disabledFields.safeMinimumUsableStock))}
                    />
                </Grid>
                <Grid
                    key={`row-processingRatio-${index}`}
                    size={columns[2].colSpan}
                    p={1}
                >
                    <InputSingleline
                        name="processingRatio"
                        type="number"
                        min={1}
                        centerAligned
                        onChange={(newValue) => {
                            return onItemCellChange({
                                index,
                                item,
                                handleChange,
                                newValue: {
                                    processingRatio: Number(newValue),
                                },
                            })
                        }}
                        className={classes.input}
                        value={item?.processingRatio}
                        required={required}
                        disabled={isCRTProduct || (!showFields.processingRatio
                            && (disabled || disabledFields.processingRatio))}
                    />
                </Grid>
            </>
        )
    }, [
        columns,
        allowedValues,
        required,
        showFields,
        disabled,
        classes,
        disabledFields,
        onItemCellChange,
    ])

    if (exists) {
        return (
            <>
                {value && (
                    <AdvancedList<ProductMetricsType>
                        onChange={onChange}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        value={value}
                        renderItem={renderItem}
                        addButtonLabel="new container"
                        columns={columns}
                        validateForAdd={validateItemForAdd}
                    />
                )}
            </>
        )
    }

    return (
        <AdvancedList<ProductMetricsType>
            onChange={onChange}
            defaultValue={defaultValue}
            disabled={disabled}
            value={value}
            renderItem={renderItem}
            addButtonLabel="new container"
            columns={columns}
            validateForAdd={validateItemForAdd}
        />
    )
}

ProductMetrics.defaultProps = defaultProps

export default ProductMetrics
